/**
 * Client-side migration utility
 * Runs once when an admin logs in to migrate data to the new multi-tenant format
 */

import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where
} from 'firebase/firestore';
import { db } from './firebase';

// Map of known users: name -> { email, uid }
const KNOWN_USERS = {
  'Jason': {
    email: 'jason.brannon@entrusted.com',
    uid: '2uAU84YujZTzaQI8RZSRb8zh0m02'
  },
  'Matt': {
    email: 'matt.garvey@entrusted.com',
    uid: 'XMTwVzM3yqWsJsEC0xR6JmfkeS82'
  },
  'Billy': {
    email: 'billy.baker@entrusted.com',
    uid: null // Not signed up yet
  }
};

function getUidByName(name) {
  const user = KNOWN_USERS[name];
  if (!user) {
    console.log(`Unknown user name: ${name}`);
    return null;
  }
  return user.uid;
}

async function ensureUserDocuments(currentUser) {
  console.log('=== Creating User Documents ===');
  let created = 0;
  let skipped = 0;

  // Only create/verify the current user's document
  // Other users will get their documents created when they log in via useAuth
  if (!currentUser?.uid) {
    console.log('No current user provided, skipping user document creation');
    return { created: 0, skipped: 0 };
  }

  const userDocRef = doc(db, 'users', currentUser.uid);
  const userDoc = await getDoc(userDocRef);

  if (userDoc.exists()) {
    console.log(`User doc already exists for ${currentUser.email}`);
    skipped = 1;
  } else {
    console.log(`Creating user doc for ${currentUser.email} (${currentUser.uid})`);
    const firstName = currentUser.displayName?.split(' ')[0] || currentUser.email?.split('@')[0];
    await setDoc(userDocRef, {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName || firstName,
      firstName: firstName,
      managerId: null,
      supervisorIds: [],
      taskManagementEnabled: true,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    created = 1;
  }

  return { created, skipped };
}

async function migrateTasks() {
  console.log('=== Migrating Tasks ===');
  const tasksSnapshot = await getDocs(collection(db, 'tasks'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of tasksSnapshot.docs) {
    const data = docSnap.data();

    if (data.userId) {
      skipped++;
      continue;
    }

    const owner = data.owner;
    if (!owner) {
      console.log(`Task ${docSnap.id} has no owner, skipping`);
      skipped++;
      continue;
    }

    const uid = getUidByName(owner);
    if (!uid) {
      console.log(`Could not find UID for task owner: ${owner}`);
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'tasks', docSnap.id), {
      userId: uid,
      legacyOwner: owner
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateProjects() {
  console.log('=== Migrating Projects ===');
  const projectsSnapshot = await getDocs(collection(db, 'projects'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of projectsSnapshot.docs) {
    const data = docSnap.data();

    if (data.userId) {
      skipped++;
      continue;
    }

    const owner = data.owner;
    if (!owner) {
      const uid = getUidByName('Jason');
      if (uid) {
        await updateDoc(doc(db, 'projects', docSnap.id), {
          userId: uid,
          legacyOwner: 'Jason (default)'
        });
        migrated++;
      }
      continue;
    }

    const uid = getUidByName(owner);
    if (!uid) {
      console.log(`Could not find UID for project owner: ${owner}`);
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'projects', docSnap.id), {
      userId: uid,
      legacyOwner: owner
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateLinks() {
  console.log('=== Migrating Links (Habits) ===');
  const linksSnapshot = await getDocs(collection(db, 'links'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of linksSnapshot.docs) {
    const data = docSnap.data();
    const assignments = data.assignments || {};

    // Skip if already has creatorId (migrated)
    if (data.creatorId) {
      skipped++;
      continue;
    }

    // Check if already migrated (keys are UIDs)
    const keys = Object.keys(assignments);
    if (keys.length > 0 && keys[0].length > 20) {
      skipped++;
      continue;
    }

    // Convert assignments from name-based to UID-based
    const newAssignments = {};
    let hasAssignments = false;

    for (const [name, value] of Object.entries(assignments)) {
      const uid = getUidByName(name);
      if (uid) {
        newAssignments[uid] = value;
        hasAssignments = true;
      } else {
        console.log(`Link ${docSnap.id}: Could not find UID for assignment name "${name}"`);
      }
    }

    const creatorId = getUidByName('Jason');

    const updateData = {
      legacyAssignments: assignments,
      creatorId: creatorId
    };

    if (hasAssignments) {
      updateData.assignments = newAssignments;
    }

    await updateDoc(doc(db, 'links', docSnap.id), updateData);
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateOneOnOneRelationships() {
  console.log('=== Migrating 1:1 Relationships ===');
  const relationshipsSnapshot = await getDocs(collection(db, 'oneOnOneRelationships'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of relationshipsSnapshot.docs) {
    const data = docSnap.data();

    if (data.participants && data.participants.length > 0) {
      skipped++;
      continue;
    }

    const createdBy = data.createdBy;
    const createdByUid = getUidByName(createdBy);

    if (!createdByUid) {
      console.log(`Could not find UID for relationship createdBy: ${createdBy}`);
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'oneOnOneRelationships', docSnap.id), {
      createdBy: createdByUid,
      legacyCreatedBy: createdBy,
      participants: [createdByUid],
      linkedUserId: null,
      linkedUserEmail: null
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateTopics() {
  console.log('=== Migrating Topics ===');
  const topicsSnapshot = await getDocs(collection(db, 'topics'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of topicsSnapshot.docs) {
    const data = docSnap.data();

    if (data.addedByUid) {
      skipped++;
      continue;
    }

    const addedBy = data.addedBy;
    const addedByUid = getUidByName(addedBy);

    if (!addedByUid) {
      console.log(`Could not find UID for topic addedBy: ${addedBy}`);
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'topics', docSnap.id), {
      addedByUid: addedByUid,
      legacyAddedBy: addedBy
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateActionItems() {
  console.log('=== Migrating Action Items ===');
  const actionItemsSnapshot = await getDocs(collection(db, 'actionItems'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of actionItemsSnapshot.docs) {
    const data = docSnap.data();

    if (data.assignedToUid) {
      skipped++;
      continue;
    }

    const assignedTo = data.assignedTo;
    const assignedToUid = getUidByName(assignedTo);

    if (!assignedToUid) {
      console.log(`Could not find UID for action item assignedTo: ${assignedTo}`);
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'actionItems', docSnap.id), {
      assignedToUid: assignedToUid,
      createdByUid: assignedToUid,
      legacyAssignedTo: assignedTo
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateGoals() {
  console.log('=== Migrating Goals ===');
  const goalsSnapshot = await getDocs(collection(db, 'goals'));
  let migrated = 0;
  let skipped = 0;

  const jasonUid = getUidByName('Jason');

  for (const docSnap of goalsSnapshot.docs) {
    const data = docSnap.data();

    if (data.userId) {
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'goals', docSnap.id), {
      userId: jasonUid,
      sharedWith: []
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migratePolicies() {
  console.log('=== Migrating Policies ===');
  const policiesSnapshot = await getDocs(collection(db, 'policies'));
  let migrated = 0;
  let skipped = 0;

  const jasonUid = getUidByName('Jason');

  for (const docSnap of policiesSnapshot.docs) {
    const data = docSnap.data();

    if (data.userId) {
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'policies', docSnap.id), {
      userId: jasonUid,
      sharedWith: []
    });
    migrated++;
  }

  return { migrated, skipped };
}

async function migrateTimeBlocks() {
  console.log('=== Migrating Time Blocks ===');
  const timeBlocksSnapshot = await getDocs(collection(db, 'timeBlocks'));
  let migrated = 0;
  let skipped = 0;

  for (const docSnap of timeBlocksSnapshot.docs) {
    const data = docSnap.data();

    if (data.userId) {
      skipped++;
      continue;
    }

    const owner = data.owner;
    if (!owner) {
      const uid = getUidByName('Jason');
      if (uid) {
        await updateDoc(doc(db, 'timeBlocks', docSnap.id), {
          userId: uid,
          legacyOwner: 'Jason (default)'
        });
        migrated++;
      }
      continue;
    }

    const uid = getUidByName(owner);
    if (!uid) {
      console.log(`Could not find UID for time block owner: ${owner}`);
      skipped++;
      continue;
    }

    await updateDoc(doc(db, 'timeBlocks', docSnap.id), {
      userId: uid,
      legacyOwner: owner
    });
    migrated++;
  }

  return { migrated, skipped };
}

// Check if migration has already been run
async function checkMigrationStatus() {
  // Check if any users collection documents exist with the new format
  const jasonDoc = await getDoc(doc(db, 'users', '2uAU84YujZTzaQI8RZSRb8zh0m02'));
  return jasonDoc.exists();
}

export async function runMigration(onProgress, currentUser) {
  const results = {
    users: { created: 0, skipped: 0 },
    tasks: { migrated: 0, skipped: 0 },
    projects: { migrated: 0, skipped: 0 },
    links: { migrated: 0, skipped: 0 },
    relationships: { migrated: 0, skipped: 0 },
    topics: { migrated: 0, skipped: 0 },
    actionItems: { migrated: 0, skipped: 0 },
    goals: { migrated: 0, skipped: 0 },
    policies: { migrated: 0, skipped: 0 },
    timeBlocks: { migrated: 0, skipped: 0 }
  };

  console.log('========================================');
  console.log('  Multi-Tenant Migration');
  console.log('========================================');

  onProgress?.('Creating user documents...');
  results.users = await ensureUserDocuments(currentUser);

  onProgress?.('Migrating tasks...');
  results.tasks = await migrateTasks();

  onProgress?.('Migrating projects...');
  results.projects = await migrateProjects();

  onProgress?.('Migrating links (habits)...');
  results.links = await migrateLinks();

  onProgress?.('Migrating 1:1 relationships...');
  results.relationships = await migrateOneOnOneRelationships();

  onProgress?.('Migrating topics...');
  results.topics = await migrateTopics();

  onProgress?.('Migrating action items...');
  results.actionItems = await migrateActionItems();

  onProgress?.('Migrating goals...');
  results.goals = await migrateGoals();

  onProgress?.('Migrating policies...');
  results.policies = await migratePolicies();

  onProgress?.('Migrating time blocks...');
  results.timeBlocks = await migrateTimeBlocks();

  console.log('========================================');
  console.log('  Migration Complete!');
  console.log('========================================');
  console.log('Results:', results);

  return results;
}

export { checkMigrationStatus };
